import React, {Fragment} from "react";
import shopApi from "../api/shop";
import {GetServerSideProps, NextPage} from "next";
import {domainUrl} from "../helper";
import MainLayout, {ICurrencies, ILayout} from "../components/ForCWV/Wrappers/MainLayout";
import Head from "next/head";
import CustomShopProduct from "../components/ForCWV/InnerPageProduct/CustomShopProduct";
import {IProduct, TranslationsType} from "../types/homePageTypes";
import {CardInnerPageTranslations} from "../services/translationKeys";
import {layoutProps} from "../services/layoutProps";
import ReCaptchaWrapper from "../components/shared/ReCaptchaWrapper";
import {useRouter} from "next/router";

export interface ICustomerGroupPrices {
    id: number;
    qty: string;
    value_type: string;
    value: string;
    product_id: number;
    customer_group_id: string;
    created_at: string;
    updated_at: string
}

interface IProductInner extends ILayout {
    translationsData: TranslationsType;
    dbName: string;
    breadcrumb: Array<{ title: string; url: string }>;
    customerGroupPrices: Array<ICustomerGroupPrices>;
    domain: string;
    productSlug: string;
    selectedRate: ICurrencies;
    serverSideProduct: IProduct;
    backOrderValue: string;
    selectedLocale: string;
    schemaBrandName: string;
    showDetailsInFront: boolean;
    relatedProducts: Array<IProduct>;
    currencies: Array<ICurrencies>;
    getBuilderData: any;
    showBuilderData: boolean;
    recaptchaActive: string;
    recaptchaKey: string;
    siteName: string
}

const ProductInnerPage: NextPage<IProductInner> = (pageProps) => {
    const {
        productSlug,
        serverSideProduct,
        relatedProducts,
        customerGroupPrices,
        breadcrumb,
        schemaBrandName,
        showDetailsInFront,
        dbName,
        translationsData,
        backOrderValue,
        domain,
        selectedRate,
        selectedLocale: locale,
        currencies = [],
        getBuilderData,
        showBuilderData,
        isMobile,
        recaptchaActive,
        recaptchaKey,
        siteName
    } = pageProps
    const router = useRouter();

    const [{
        code: selectedCurrCode = "",
        symbol: selectedCurrSymbol = "",
        exchange_rate: {rate: selectedCurrRate = "1"} = {},
    } = {}] = currencies

    const selectedCurrItems = {
        selectedCurrCode,
        selectedCurrRate,
        selectedCurrSymbol
    }

    const builderProps = {
        getBuilderData,
        domain,
        dbName,
        translationsData,
        selectedLocale: locale,
        currencies,
        backOrderValue,
        selectedCurrItems,
        isMobile,
        recaptchaActive
    }

    const {
        flats = [],
        yoast_products = [],
    } = serverSideProduct || {};

    const [{
        meta_title: m_title = flats[0].name,
        meta_description: m_desc = flats[0].short_description,
        meta_keywords: m_keyword = "",
        url_key: m_url = "",
        meta_image: m_img = ""
    } = {}] = flats

    const [{
        yoast_facebook_title: FB_title = m_title,
        yoast_facebook_description: FB_desc = m_desc,
        yoast_twitter_title: TW_title = m_keyword,
        yoast_twitter_description: TW_desc = m_desc,
        yoast_focus_keyword: y_keyword = m_keyword
    } = {}] = yoast_products
    const canonicalUrl = `${dbName}/${router.locale !== "catchAll" ? locale + "/" : ""}${m_url}`

    return (
        <ReCaptchaWrapper recaptchaActive={recaptchaActive} recaptchaKey={recaptchaKey} >
            <MainLayout {...pageProps}>
                <Fragment>
                    <Head>
                        <title>{m_title}</title>
                        <meta name="title" content={m_title}/>
                        <meta name="description" content={m_desc}/>
                        <meta name="keywords" content={m_keyword}/>
                        <meta name="generator" content="Zegashop.com"/>
                        <meta name="robots" content="index, follow"/>
                        <meta name="twitter:title" content={TW_title}/>
                        <meta name="twitter:description" content={TW_desc}/>
                        <meta name="twitter:image" content={domainUrl(`${dbName}${m_img}`)}/>
                        <meta property="og:title" content={FB_title}/>
                        <meta property="og:description" content={FB_desc}/>
                        <meta property="og:keywords" content={y_keyword}/>
                        <meta property="og:image" content={domainUrl(`${dbName}${m_img}`)}/>
                        <meta property="og:url" content={domainUrl(canonicalUrl)}/>
                        <meta property="og:locale" content={locale}/>
                        <meta property="og:site_name" content={siteName}/>
                        <link rel="canonical" href={domainUrl(canonicalUrl)}/>
                    </Head>
                    <CustomShopProduct
                        translationsData={translationsData}
                        breadcrumb={breadcrumb}
                        backOrderValue={backOrderValue}
                        dbName={dbName}
                        domain={domain}
                        productSlug={productSlug}
                        selectedRate={selectedRate}
                        selectedLocale={locale}
                        showDetailsInFront={showDetailsInFront}
                        product={serverSideProduct}
                        customerGroupPrices={customerGroupPrices}
                        schemaBrandName={schemaBrandName}
                        relatedProducts={relatedProducts}
                        selectedCurrItems={selectedCurrItems}
                        builderProps={builderProps}
                        showBuilderData={showBuilderData}
                    />
                </Fragment>
            </MainLayout>
        </ReCaptchaWrapper>
    );
};
export default ProductInnerPage;

export const getServerSideProps: GetServerSideProps = async ({locale, req, query: {productSlug}}) => {
    const typedReq = req as unknown as {
        m: { [key: string]: any }[],
        defaultDB: { db_name: string },
        assets: { logo_image: string },
        ipv4: string
    }
    const wrapperProps = await layoutProps({
        requestHeaders: req.headers,
        requestDbName: typedReq.defaultDB.db_name,
        requestModels: typedReq.m,
        requestLogo: typedReq.assets.logo_image,
        nextJSLocale: locale,
        translationsObject: CardInnerPageTranslations,
        ipv4: typedReq.ipv4,
        forEachPageBuilder: true
    })
    const { maintenanceInfo, suspendStatus, selectedLocale, selectedRate, translationsData: {Home: HomeIntl}, isMobile, themeOptions } = wrapperProps;
    if (String(suspendStatus) === "469") {
        return {
            redirect: {destination: '/suspend', permanent: false},
        }
    }
    if (maintenanceInfo?.is_maintenance_on) {
        return {
            redirect: { destination: '/maintenance', permanent: false },
        }
    }
    const dbName = req.headers["x-forwarded-host"];

    const [serverSideProduct] = [] = await shopApi.getProductBySlugAsync(productSlug as string, {
        locale: selectedLocale,
        selectedRate: selectedRate,
    }, typedReq.m);

    if (!serverSideProduct) {
        return {
            notFound: true
        }
    }

    const {
        category_ids: [catId] = [],
        id: productId = 0,
        flats: [{name: productName = "", url_key = ""} = {}] = [],
        images: [serversidePath = ""] = []
    } = {} = serverSideProduct || {}

    const relatedProducts = await shopApi.getRelatedProductsAsync(
        {
            category_id: catId,
            product_id: productId,
            locale: selectedLocale,
            currency: selectedRate,
            limit: 8,
        },
        typedReq.m
    );
    const customerGroupPrices = await shopApi.getCustomerGroups(typedReq.m) || {}
    const getBuilderData = await shopApi.getBuilderDataAsync(
        serverSideProduct?.id + 200, selectedLocale, typedReq.m, req.headers["x-forwarded-host"], typedReq.defaultDB.db_name, selectedRate, isMobile, themeOptions
    ) || []

    const breadcrumb = [
        {title: HomeIntl, url: `/`},
        {title: productName, url: `https://${dbName}${url_key}`}
    ]

    const {attribute_value: schemaBrandName = ""} = serverSideProduct?.details?.find((el: any) => el.attribute[0]?.code === "brand") || {}
    const visibleDetails = serverSideProduct?.details?.filter(
        (el: any) => +el.attribute?.[0]?.is_visible_on_front === 1
    ) || [];
    const showDetailsInFront = visibleDetails.length > 6;
    serverSideProduct.flats = serverSideProduct?.flats?.map((el: any) => {
        return {
            ...el,
            meta_image: !!serversidePath && serversidePath.includes("_next") ? `/storage/${wrapperProps.domain}/configuration/logo/logo.webp` : serversidePath
        }
    })

    return {
        props: {
            productSlug,
            serverSideProduct,
            relatedProducts,
            customerGroupPrices,
            breadcrumb,
            schemaBrandName,
            showDetailsInFront: showDetailsInFront ?? null,
            getBuilderData,
            showBuilderData: Object.values(getBuilderData).some((lll) => (lll as Array<any>)?.length > 0),
            ...wrapperProps,
        },
    };
}
